import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const COPY = {
  en: {
    title: "Delete your account?",
    body: "This will permanently delete your profile, birth chart, journal entries, life letter, mission progress, and every other piece of data you've shared with Vedari. This action cannot be undone.",
    confirm_label: "Type DELETE to confirm",
    confirm_ph: "DELETE",
    cancel: "Cancel",
    confirm: "Delete forever",
    deleting: "Deleting…",
    success: "Your account has been deleted.",
  },
  hi: {
    title: "खाता हटाएँ?",
    body: "यह आपकी प्रोफ़ाइल, जन्म कुंडली, डायरी प्रविष्टियाँ, जीवन-पत्र, मिशन प्रगति और Vedari के साथ साझा किया गया हर डेटा स्थायी रूप से हटा देगा। यह क्रिया वापस नहीं की जा सकती।",
    confirm_label: "पुष्टि के लिए DELETE टाइप करें",
    confirm_ph: "DELETE",
    cancel: "रद्द करें",
    confirm: "हमेशा के लिए हटाएँ",
    deleting: "हटा रहे हैं…",
    success: "आपका खाता हटा दिया गया है।",
  },
};

/**
 * Confirmation modal for permanent account deletion. Requires the user to
 * type the word DELETE to enable the destructive button. On success, clears
 * the session and routes back to /.
 */
export default function DeleteAccountModal({ open, onClose }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { lang } = useLang();
  const c = COPY[lang === "hi" ? "hi" : "en"];
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const ready = text.trim() === "DELETE" && !busy;

  const doDelete = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      await api.delete("/me/account", { data: { confirm: "DELETE" } });
      try { await logout(); } catch {}
      toast.success(c.success);
      navigate("/", { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || (lang === "hi" ? "हटा नहीं सके।" : "Could not delete.");
      toast.error(typeof msg === "string" ? msg : (lang === "hi" ? "हटा नहीं सके।" : "Could not delete."));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={() => !busy && onClose()}
      data-testid="delete-account-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl p-6 animate-gentle-rise"
        style={{ backgroundColor: "var(--vedari-cream)", border: "1px solid var(--vedari-rust)" }}
      >
        <div className="flex items-center gap-3 mb-3">
          <AlertTriangle size={22} style={{ color: "var(--vedari-rust)" }} />
          <h3 className="font-display text-2xl">{c.title}</h3>
        </div>
        <p className="text-body leading-relaxed font-serif" style={{ color: "var(--vedari-ink-soft)" }}>
          {c.body}
        </p>

        <label className="block text-body-sm font-medium mt-5 mb-2" style={{ color: "var(--vedari-ink-soft)" }}>
          {c.confirm_label}
        </label>
        <input
          className="vedari-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={c.confirm_ph}
          autoFocus
          data-testid="delete-account-input"
        />

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={busy}
            className="vedari-btn-secondary flex-1"
            data-testid="delete-account-cancel-btn"
          >
            {c.cancel}
          </button>
          <button
            onClick={doDelete}
            disabled={!ready}
            data-testid="delete-account-confirm-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-body font-medium transition-all disabled:opacity-40"
            style={{
              backgroundColor: ready ? "var(--vedari-rust)" : "var(--vedari-surface)",
              color: ready ? "var(--vedari-cream)" : "var(--vedari-ink-soft)",
              border: `1.5px solid ${ready ? "var(--vedari-rust)" : "var(--vedari-divider)"}`,
            }}
          >
            <Trash2 size={16} /> {busy ? c.deleting : c.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}
